import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import type { UserOut } from "@shop-ledger/shared-types";
import { authenticateWithPasskey } from "@shop-ledger/ui-components";
import * as authApi from "./authApi";
import { ApiError } from "../lib/apiClient";
import { clearAuthSession, loadAuthSession, saveAuthSession, type AuthSession } from "../lib/storage";

export type AuthStatus = "unauthenticated" | "authenticated";

export interface AuthContextValue {
  status: AuthStatus;
  /** The raw persisted session — `/pos/*`'s `DbProvider`/`SyncProvider` (ported from
   * `pos-pwa`) read `session.user.tenant_id`/`session.accessToken`/`session.refreshToken`
   * directly, the same way they did in the standalone app; every other back-office call
   * site should keep using `ensureFreshToken()` below instead of reading this raw token,
   * since it doesn't proactively rotate. */
  session: AuthSession | null;
  user: UserOut | null;
  loginPending: boolean;
  loginError: string | null;
  login: (email: string, password: string) => Promise<void>;
  /** Consumes an SSO handoff code (from `?code=` on this app's `/sso` route) and
   * establishes a real session, same as `login()` but with no password prompt — the
   * caller already proved identity in the source app (or, for the marketing-site demo
   * link, the backend's `GET /auth/demo/handoff` minted the code itself). */
  loginWithSsoCode: (code: string) => Promise<void>;
  /** Consumes an impersonation handoff code (from `?code=` on this app's `/impersonate`
   * route) minted by `platform-admin`'s "View as tenant" action. Establishes a real
   * tenant session with NO refresh token (§9.1: impersonation sessions are short-lived
   * and non-renewable by design) — `session.refreshToken` is `null` for the duration. */
  loginWithImpersonationCode: (code: string) => Promise<void>;
  /** Usernameless passkey login (Face ID/fingerprint/security key) — no email typed
   * anywhere, see `@shop-ledger/ui-components`'s `authenticateWithPasskey`. */
  loginWithPasskey: () => Promise<void>;
  /** True whenever the current session is an impersonation session (no refresh token) —
   * drives the "you are being impersonated by a platform operator" banner. */
  isImpersonating: boolean;
  /** Cross-app links (`CrossAppLinks.tsx`) call this to get a one-time code, then
   * redirect to `{targetAppUrl}/sso?code=...`. Requires an existing session. Only
   * `"dashboard"` (→ `ecommerce-store`) is used post-merge — POS is an in-app route now,
   * not a cross-app SSO target — but the parameter stays typed against the full
   * `LoginClient` union since that's what `SSOInitiateRequest.target_client` accepts. */
  initiateSso: (targetClient: "dashboard" | "pos") => Promise<string>;
  logout: () => Promise<void>;
  /**
   * Returns a currently-valid access token, transparently rotating it via
   * `POST /auth/refresh` first if the stored token is within 30s of expiry (or already
   * expired). Every back-office `api/*Api.ts` call site awaits this instead of reading a
   * raw token off context directly. `/pos/*`'s own sync engine does its own thing (tolerates
   * a stale token indefinitely per §2's offline exception, only refreshes reactively off a
   * 401 — see `pos/sync/SyncProvider.tsx`), which is why `updateTokens` below exists
   * alongside this rather than replacing it.
   */
  ensureFreshToken: () => Promise<string>;
  /** Ported from `pos-pwa`'s `AuthContext` — called by `/pos/*`'s sync engine
   * (`pos/sync/SyncProvider.tsx`) after a successful `POST /auth/refresh` triggered by a
   * 401 on a push/pull call, so subsequent sync calls (and a future page reload) use the
   * rotated token pair. Never called from the till's own local-write path — that path
   * never touches the network at all. */
  updateTokens: (accessToken: string, refreshToken: string, expiresAt: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const REFRESH_SKEW_MS = 30_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(() => loadAuthSession());
  const [loginPending, setLoginPending] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  // Guards against two concurrent callers both kicking off a refresh (e.g. two panels
  // fetching data on the same page load) — the second awaiter reuses the first's in-flight
  // promise instead of racing a second `/auth/refresh` call (which would otherwise
  // denylist the first call's rotated token before it's even used, per the backend's
  // single-use refresh-rotation design).
  const refreshInFlight = useRef<Promise<string> | null>(null);

  const establishSession = useCallback(
    async (tokenPair: { access_token: string; refresh_token: string | null; expires_at: string }) => {
      const user = await authApi.fetchCurrentUser(tokenPair.access_token);
      const next: AuthSession = {
        accessToken: tokenPair.access_token,
        refreshToken: tokenPair.refresh_token,
        expiresAt: tokenPair.expires_at,
        user,
      };
      saveAuthSession(next);
      setSession(next);
    },
    [],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      setLoginPending(true);
      setLoginError(null);
      try {
        const tokenPair = await authApi.login(email, password);
        await establishSession(tokenPair);
      } catch (error) {
        const message =
          error instanceof ApiError
            ? error.message
            : error instanceof Error
              ? "Could not reach the server. Check your connection and try again."
              : "Login failed";
        setLoginError(message);
        throw error;
      } finally {
        setLoginPending(false);
      }
    },
    [establishSession],
  );

  const loginWithSsoCode = useCallback(
    async (code: string) => {
      setLoginPending(true);
      setLoginError(null);
      try {
        const tokenPair = await authApi.ssoConsume(code);
        await establishSession(tokenPair);
      } catch (error) {
        const message = error instanceof ApiError ? error.message : "This sign-in link has expired. Please log in again.";
        setLoginError(message);
        throw error;
      } finally {
        setLoginPending(false);
      }
    },
    [establishSession],
  );

  const loginWithPasskey = useCallback(async () => {
    setLoginPending(true);
    setLoginError(null);
    try {
      const { session_id, options } = await authApi.webauthnLoginBegin();
      const credential = await authenticateWithPasskey(options);
      const tokenPair = await authApi.webauthnLoginComplete({
        session_id,
        credential: credential as Record<string, unknown>,
      });
      await establishSession(tokenPair);
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : error instanceof Error && error.name === "PasskeyCancelledError"
            ? "No passkey was found for this site, or the request was cancelled or timed out. Try signing in with your password instead."
            : "Could not sign in with a passkey.";
      setLoginError(message);
      throw error;
    } finally {
      setLoginPending(false);
    }
  }, [establishSession]);

  const loginWithImpersonationCode = useCallback(
    async (code: string) => {
      setLoginPending(true);
      setLoginError(null);
      try {
        const res = await authApi.impersonationConsume(code);
        await establishSession({ access_token: res.access_token, refresh_token: null, expires_at: res.expires_at });
      } catch (error) {
        const message = error instanceof ApiError ? error.message : "This impersonation link has expired. Ask the platform operator to start a new session.";
        setLoginError(message);
        throw error;
      } finally {
        setLoginPending(false);
      }
    },
    [establishSession],
  );

  const logout = useCallback(async () => {
    // Impersonation sessions (session.refreshToken === null) have no refresh token to
    // revoke server-side — there is no separate revoke endpoint by design (§9.1: the
    // token is short-lived and non-refreshable, so it simply expires on its own).
    if (session?.refreshToken) await authApi.logout(session.refreshToken);
    clearAuthSession();
    setSession(null);
  }, [session]);

  const ensureFreshToken = useCallback(async (): Promise<string> => {
    const current = loadAuthSession();
    if (!current) throw new Error("Not authenticated");

    const msUntilExpiry = new Date(current.expiresAt).getTime() - Date.now();
    if (msUntilExpiry > REFRESH_SKEW_MS) return current.accessToken;

    const refreshToken = current.refreshToken;
    if (!refreshToken) {
      // Impersonation session nearing/past its 15-minute window — non-renewable by
      // design (§9.1), so the only path forward is a fresh impersonation from
      // platform-admin, not a refresh call this session was never issued a token for.
      clearAuthSession();
      setSession(null);
      throw new Error("This impersonation session has expired. Ask the platform operator to start a new one.");
    }

    if (!refreshInFlight.current) {
      refreshInFlight.current = (async () => {
        try {
          const rotated = await authApi.refreshTokenPair(refreshToken);
          const next: AuthSession = {
            accessToken: rotated.access_token,
            refreshToken: rotated.refresh_token,
            expiresAt: rotated.expires_at,
            user: current.user,
          };
          saveAuthSession(next);
          setSession(next);
          return next.accessToken;
        } catch (error) {
          // Refresh token itself expired/denylisted (e.g. long-idle tab) — the session is
          // no longer salvageable; force a real re-login rather than looping on 401s.
          clearAuthSession();
          setSession(null);
          throw error;
        } finally {
          refreshInFlight.current = null;
        }
      })();
    }
    return refreshInFlight.current;
  }, []);

  const initiateSso = useCallback(
    async (targetClient: "dashboard" | "pos") => {
      const token = await ensureFreshToken();
      return authApi.ssoInitiate(token, targetClient);
    },
    [ensureFreshToken],
  );

  // Ported from pos-pwa's AuthContext — see the interface doc comment above.
  const updateTokens = useCallback((accessToken: string, refreshToken: string, expiresAt: string) => {
    setSession((prev) => {
      if (!prev) return prev;
      const next: AuthSession = { ...prev, accessToken, refreshToken, expiresAt };
      saveAuthSession(next);
      return next;
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status: session ? "authenticated" : "unauthenticated",
      session,
      user: session?.user ?? null,
      loginPending,
      loginError,
      login,
      loginWithSsoCode,
      loginWithImpersonationCode,
      loginWithPasskey,
      isImpersonating: session !== null && session.refreshToken === null,
      initiateSso,
      logout,
      ensureFreshToken,
      updateTokens,
    }),
    [
      session,
      loginPending,
      loginError,
      login,
      loginWithSsoCode,
      loginWithPasskey,
      loginWithImpersonationCode,
      initiateSso,
      logout,
      ensureFreshToken,
      updateTokens,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth() must be used within an <AuthProvider>");
  return ctx;
}
